import {Popover, styled, Typography} from '@mui/material';
import {PropsWithChildren, ReactNode} from 'react';
import Tooltip from '@mui/material/Tooltip';

import {shouldNotForwardProp} from 'utils/styled';

import Info2Icon from 'components/icons/info-2.svg';

export const DEFAULT_POPOVER_WIDTH = 330;

const SPopover = styled(
  Popover,
  shouldNotForwardProp('width')
)<{width?: number}>(({theme, width}) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.background.white,
    padding: theme.spacing(2, 0),
    borderRadius: theme.shape.borderRadiusRegular,
    width: width ?? DEFAULT_POPOVER_WIDTH,
  },
}));

const SPopoverTitle = styled('div')(({theme}) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
}));

const SPopoverActions = styled(
  'div',
  shouldNotForwardProp('withDivider')
)<{withDivider?: boolean}>(({theme, withDivider}) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  padding: theme.spacing(0, 3),
  ...(withDivider && {borderTop: `1px solid ${theme.palette.divider}`, paddingTop: theme.spacing(2)}),
}));

const SInfoIcon = styled(Info2Icon)(() => ({
  cursor: 'help',
}));

export type PopoverOptionsProps = PropsWithChildren<{
  className?: string;
  anchorEl: HTMLElement | null;
  labelHelpText?: ReactNode;
  onClose: () => void;
  onExited?: () => void;
  title?: string;
  width?: number;
  footer?: ReactNode;
  withFooterDivider?: boolean;
  anchorOrigin?: {
    vertical?: 'top' | 'bottom';
    horizontal?: 'left' | 'right';
  };
  transformOrigin?: {
    vertical?: 'top' | 'bottom';
    horizontal?: 'left' | 'right';
  };
}>;

export const PopoverOptions = ({
  className,
  anchorEl,
  onClose,
  onExited,
  title,
  children,
  footer,
  anchorOrigin,
  transformOrigin,
  width,
  withFooterDivider,
  labelHelpText,
}: PopoverOptionsProps) => {
  const open = Boolean(anchorEl);

  return (
    <SPopover
      width={width}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: anchorOrigin?.vertical ?? 'bottom',
        horizontal: anchorOrigin?.horizontal ?? 'left',
      }}
      transformOrigin={{
        vertical: transformOrigin?.vertical ?? 'top',
        horizontal: transformOrigin?.horizontal ?? 'left',
      }}
      open={open}
      onClose={onClose}
      TransitionProps={{
        onExited,
      }}
      slotProps={{
        paper: {
          className,
          elevation: 3,
          sx: {
            marginTop: anchorOrigin?.vertical === 'top' ? -2 : 2,
          },
        },
      }}
    >
      {title && (
        <SPopoverTitle>
          <Typography variant='body3' color='text.secondary'>
            {title}
          </Typography>

          {labelHelpText && (
            <Tooltip title={labelHelpText} placement='top-end'>
              <SInfoIcon />
            </Tooltip>
          )}
        </SPopoverTitle>
      )}

      {children}

      {footer && <SPopoverActions withDivider={withFooterDivider}>{footer}</SPopoverActions>}
    </SPopover>
  );
};
